/**
 * The two questions the axios error interceptor has to answer about a failed
 * response, kept in a LEAF module on purpose.
 *
 * ⚠️ They lived in `api.config.ts`, which imports the stores, the router and
 * the whole config graph — so a test importing them pulled in the entire app
 * and could not run. Copying them into the test instead would have been worse:
 * a copy agrees with its assertions by construction and keeps passing while the
 * shipping code drifts. A leaf module lets the tests exercise the REAL
 * predicates.
 */
/**
 * Is this 403 "you are no longer in that workspace"?
 *
 * ⭐⭐⭐ THE REASON LIVES IN `object.error_code`. The real body is
 * `{status:'forbidden', code:403, message:'…', object:{error_code:'WORKSPACE_FORBIDDEN'}}`
 * — `code` is a NUMBER. A first attempt tested `typeof (code ?? message) ===
 * 'string'`, which was false before it compared anything, so the screen stayed
 * broken while the fix looked present in review. `SetCompanyContext::forbidden()`
 * says so in its own comment; reading the producer beats inferring from the
 * consumer.
 *
 * The `error_code` is the contract. The message is a human sentence that may be
 * translated or reworded, so it is never the test.
 *
 * Exported so its tests exercise THIS function rather than a copy of its logic.
 */
export const isWorkspaceGone = (status: number | undefined, body: any): boolean => {
  const code = body?.object?.error_code;

  return status === 403 && typeof code === 'string' && code.startsWith('WORKSPACE_');
};

/**
 * Is this 401 a late reply from a session we deliberately ended?
 *
 * `activate` revokes the previous token, so a request that left with the old one
 * and answers after the switch is not an expired session — it is an answer to a
 * question we already stopped asking. Logging the merchant out for it was D9.
 */
export const isStaleSession = (sentWith: string, currentToken: string | null): boolean =>
  Boolean(currentToken) && Boolean(sentWith) && sentWith !== `Bearer ${currentToken}`;
